import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import { APP_ROUTES, DASHBOARD_ROUTES, SIGNUP_ROUTES } from '@/api/constants/app.url';
import { PageSuspense } from '@/shared/components';
import { SignupProvider, stepsFor, useSignup } from '@/modules/signup/signup-context';

/**
 * Multi-step signup chrome — direct port of PropertyScanUI SignupLayout.jsx.
 * Stepper + content card + Back/Continue footer. Details and Verify opt out
 * of the shared footer because they carry their own primary CTA.
 */
export function SignupLayout() {
    const location = useLocation();
    const hasOwnCta = location.pathname === SIGNUP_ROUTES.details || location.pathname === SIGNUP_ROUTES.verify;

    return (
        <SignupProvider>
            <div className="flex min-h-screen flex-col bg-ink-50">
                <Stepper />
                <div className="mx-auto w-full max-w-5xl px-4 pt-4">
                    <div className={cn(
                        'border border-ink-100 bg-white p-4 sm:p-6',
                        hasOwnCta ? 'mb-6 rounded-xl' : 'rounded-t-xl',
                    )}>
                        <PageSuspense><Outlet /></PageSuspense>
                    </div>
                </div>
                <StageNav />
            </div>
        </SignupProvider>
    );
}

function Stepper() {
    const location = useLocation();
    const navigate = useNavigate();
    const { signup } = useSignup();
    const STEPS = stepsFor(signup.accountType);
    const currentIdx = STEPS.findIndex((s) => s.path === location.pathname);

    return (
        <div className="mx-auto w-[38%] max-w-5xl px-4 pt-6">
            <div className="mb-4 text-center">
                <p className="font-serif text-[18px] font-semibold text-brand-navy">PropertyScan</p>
                <p className="mt-0.5 font-ui text-[9px] tracking-wider text-ink-300">BY KMI</p>
            </div>
            <div className="flex flex-wrap gap-1 p-1 bg-ink-100/60 rounded-lg">
                {STEPS.map((s, i) => {
                    const isActive  = i === currentIdx;
                    const isDone    = i < currentIdx;
                    const isPending = i > currentIdx;
                    return (
                        <button
                            key={s.num}
                            type="button"
                            disabled={isPending}
                            onClick={() => { if (!isPending) navigate(s.path); }}
                            className={cn(
                                'focus-ring flex min-w-[80px] flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 font-ui text-[11px] transition',
                                isActive  ? 'bg-white font-semibold text-brand-navy shadow-sm'
                                : isPending ? 'cursor-not-allowed text-ink-300'
                                :             'text-ink-500 hover:text-brand-navy',
                            )}
                        >
                            <span className={cn(
                                'flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold',
                                isActive ? 'bg-brand-navy text-white'
                                : isDone   ? 'bg-emerald-300 text-emerald-900'
                                :            'bg-ink-100 text-ink-300',
                            )}>
                                {isDone ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : s.num}
                            </span>
                            {s.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function StageNav() {
    const location = useLocation();
    const navigate = useNavigate();
    const { signup } = useSignup();
    const STEPS = stepsFor(signup.accountType);
    const currentIdx = STEPS.findIndex((s) => s.path === location.pathname);

    if (currentIdx === -1) return null;
    if (location.pathname === SIGNUP_ROUTES.details || location.pathname === SIGNUP_ROUTES.verify) return null;

    const ctaLabel = currentIdx === 0 ? 'Walk me through the product'
        : currentIdx === STEPS.length - 1 ? 'Land in my dashboard'
        : 'Continue';

    const go = (delta: number) => {
        const nextIdx = currentIdx + delta;
        const step = STEPS[nextIdx];
        if (step) navigate(step.path);
        else if (nextIdx === STEPS.length) navigate(DASHBOARD_ROUTES.HOME);
    };

    return (
        <div className="mx-auto w-full max-w-5xl px-4 pb-8">
            <div className="flex items-center justify-between gap-3 rounded-b-xl border border-t-0 border-ink-100 bg-white px-4 py-4 sm:px-6">
                <button
                    type="button"
                    onClick={() => (currentIdx === 0 ? navigate(APP_ROUTES.ROOT) : go(-1))}
                    className="focus-ring inline-flex items-center gap-2 rounded-md px-4 py-2 font-ui text-sm font-medium text-ink-500 transition hover:bg-ink-50 hover:text-brand-navy"
                >
                    <ArrowLeft className="h-4 w-4" /> {currentIdx === 0 ? 'Home' : 'Back'}
                </button>
                <button
                    type="button"
                    onClick={() => go(1)}
                    className="focus-ring inline-flex items-center gap-2 rounded-md bg-brand-navy px-5 py-2.5 font-ui text-sm font-semibold text-white shadow-sm transition hover:bg-brand-navy-600"
                >
                    {ctaLabel} <ArrowRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
